import React, { useEffect, useState } from 'react';
import { XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

const getFullUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  const apiUrl = import.meta.env.VITE_APP_API_URL || 'http://localhost:3001/api';
  const cleanApiUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
  return `${cleanApiUrl}/membership${url}`;
};

const DocumentViewerModal = ({ isOpen, onClose, docUrl, docName }) => {
  const fullUrl = getFullUrl(docUrl);

  // Files uploaded before uploads were kept in file-service no longer exist (404); say so instead
  // of showing a broken image or a JSON error inside the PDF frame.
  const [missingUrl, setMissingUrl] = useState(null);
  useEffect(() => {
    if (!isOpen || !fullUrl) return undefined;
    let cancelled = false;
    fetch(fullUrl, { method: 'HEAD' })
      .then((r) => { if (!cancelled && r.status === 404) setMissingUrl(fullUrl); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen, fullUrl]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !docUrl) return null;

  const missing = missingUrl === fullUrl;

  // Determine file type
  const isImage = /\.(jpg|jpeg|png|gif|webp|svg)($|\?)/i.test(fullUrl);
  const isPdf = /\.(pdf)($|\?)/i.test(fullUrl);

  return (
    <div className="fixed inset-0 z-[300] flex flex-col justify-between bg-black/90 backdrop-blur-sm animate-fadeIn">
      {/* Top Toolbar */}
      <div className="flex items-center justify-between px-6 py-4 bg-black/40 text-white z-10 border-b border-white/10">
        <div className="min-w-0">
          <h3 className="text-sm font-medium truncate pr-4">{docName || 'Document Viewer'}</h3>
          <p className="text-xs text-gray-400 font-medium mt-0.5">Press ESC or click close to return</p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href={fullUrl}
            download={docName || 'document'}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-gray-300 hover:text-white hover:bg-white/10 rounded-full transition-all flex items-center justify-center"
            title="Open in new tab / Download"
          >
            <ArrowDownTrayIcon className="w-5 h-5" />
          </a>
          <button
            onClick={onClose}
            className="p-2 text-gray-300 hover:text-white hover:bg-white/10 rounded-full transition-all flex items-center justify-center"
            title="Close viewer"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main Viewer Body */}
      <div className="flex-1 flex items-center justify-center p-4 md:p-8 overflow-auto">
        <div className="max-w-5xl w-full h-full flex items-center justify-center">
          {missing ? (
            <div className="bg-white/15 backdrop-blur-md border border-white/20 p-8 rounded-xl text-center text-white max-w-sm">
              <p className="text-sm font-medium mb-2">This file is no longer available.</p>
              <p className="text-xs text-white/70">It was uploaded before documents were kept in permanent storage. Please upload it again.</p>
            </div>
          ) : isImage ? (
            <img
              src={fullUrl}
              alt={docName || 'Document'}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl animate-scaleIn bg-white/5"
            />
          ) : isPdf ? (
            <iframe
              src={`${fullUrl}#toolbar=0`}
              title={docName || 'PDF Viewer'}
              className="w-full h-[78vh] rounded-xl border-0 shadow-2xl bg-white"
            />
          ) : (
            <div className="bg-white/15 backdrop-blur-md border border-white/20 p-8 rounded-xl text-center text-white max-w-sm">
              <p className="text-sm font-medium mb-4">Preview not available for this file type.</p>
              <a
                href={fullUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-5 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium rounded-xl transition-all shadow-lg"
              >
                Open in New Tab
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentViewerModal;
